import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Loader2, Plus, Search, FileText, Trash2, Edit2, Calendar, Upload } from 'lucide-react';
import { getMeetingMinutes, createContent, updateContent, deleteContent, uploadAsset, apiClient } from '../../services/api';
import { toast } from 'sonner';

const AdminMinutesPage = () => {
    const [minutes, setMinutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Form dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        date: '',
        contentType: 'PDF',
        content: '',
        url: ''
    });

    useEffect(() => {
        fetchMinutes();
    }, []);

    const fetchMinutes = async () => {
        setLoading(true);
        try {
            const response = await getMeetingMinutes();
            const minutesData = response.data.minutes || [];
            // Sort by date descending
            minutesData.sort((a, b) => new Date(b.date) - new Date(a.date));
            setMinutes(minutesData);
        } catch (error) {
            console.error('Failed to fetch minutes:', error);
            toast.error('Failed to load meeting minutes');
        } finally {
            setLoading(false);
        }
    };

    const filteredMinutes = minutes.filter((m) =>
        m.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleOpenAdd = () => {
        setEditingItem(null);
        setFormData({
            title: '',
            date: new Date().toISOString().split('T')[0],
            contentType: 'PDF',
            content: '',
            url: ''
        });
        setDialogOpen(true);
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);
        setFormData({
            title: item.title,
            date: item.date || '',
            contentType: item.contentType || (item.url ? 'PDF' : 'TEXT'),
            content: item.content || '',
            url: item.url || ''
        });
        setDialogOpen(true);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            toast.error('Please upload a PDF file');
            return;
        }

        // Check file size (50MB = 52428800 bytes)
        if (file.size > 52428800) {
            toast.error('File size exceeds 50MB limit');
            return;
        }

        setUploading(true);
        try {
            // For files larger than 10MB, use direct S3 upload
            if (file.size > 10485760) {
                // Get pre-signed upload URL
                const urlResponse = await apiClient.post('/admin/assets/upload-url', null, {
                    params: {
                        filename: file.name,
                        content_type: file.type
                    }
                });

                const { upload_url, s3_key } = urlResponse.data;

                // Upload directly to S3
                await fetch(upload_url, {
                    method: 'PUT',
                    body: file,
                    headers: {
                        'Content-Type': file.type
                    }
                });

                setFormData((prev) => ({ ...prev, url: s3_key }));
                toast.success('Large file uploaded successfully');
            } else {
                // For smaller files, use the regular upload endpoint
                const response = await uploadAsset(file);
                setFormData((prev) => ({ ...prev, url: response.data.url }));
                toast.success('File uploaded successfully');
            }
        } catch (error) {
            console.error('Upload failed:', error);
            toast.error('Failed to upload file');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate PDF has been uploaded
        if (formData.contentType === 'PDF' && !formData.url) {
            toast.error('Please upload a PDF file');
            return;
        }

        setFormLoading(true);
        try {
            const payload = {
                type: 'MINUTES',
                ...formData
            };

            if (editingItem) {
                await updateContent(editingItem.PK, editingItem.SK, payload);
                toast.success('Minute updated successfully');
            } else {
                await createContent(payload);
                toast.success('Minute created successfully');
            }
            setDialogOpen(false);
            fetchMinutes();
        } catch (error) {
            console.error('Failed to save minute:', error);
            toast.error('Failed to save minute. Please try again.');
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Are you sure you want to delete "${item.title}"?`)) return;

        try {
            await deleteContent(item.PK, item.SK);
            toast.success('Minute deleted successfully');
            fetchMinutes();
        } catch (error) {
            console.error('Failed to delete minute:', error);
            toast.error('Failed to delete minute');
        }
    };

    if (loading && minutes.length === 0) {
        return (
            <div className="p-6 md:p-12 flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-12" data-testid="admin-minutes-page">
            <div className="max-w-5xl">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-gray-900">Meeting Minutes</h1>
                        <p className="text-gray-600 mt-1">Manage minutes from lodge meetings.</p>
                    </div>
                    <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleOpenAdd} data-testid="add-minute-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Minute
                    </Button>
                </div>

                {/* Search */}
                <Card className="border-gray-100 mb-6">
                    <CardContent className="p-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder="Search minutes by title..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9"
                                data-testid="minute-search-input"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Table */}
                <Card className="border-gray-100">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-gray-50">
                                    <TableHead>Title</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredMinutes.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                                            <FileText className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                                            No minutes found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredMinutes.map((item) => (
                                        <TableRow key={item.id} data-testid={`minute-row-${item.id}`}>
                                            <TableCell className="font-medium">{item.title}</TableCell>
                                            <TableCell className="text-gray-500">
                                                {item.date ? new Date(item.date).toLocaleDateString('en-GB') : 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                <span className={`text-xs px-2 py-1 rounded ${item.contentType === 'PDF' || item.url ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                                                    }`}>
                                                    {item.contentType || (item.url ? 'PDF' : 'TEXT')}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(item)}>
                                                        <Edit2 className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item)} className="text-red-600 hover:text-red-700">
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{editingItem ? 'Edit Minute' : 'Add New Minute'}</DialogTitle>
                        <DialogDescription>
                            Enter the details for the meeting minute.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="title">Title</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="e.g. Regular Meeting - 18 January 2026"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="date">Meeting Date</Label>
                                    <Input
                                        id="date"
                                        type="date"
                                        value={formData.date}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="contentType">Content Type</Label>
                                    <Select
                                        value={formData.contentType}
                                        onValueChange={(val) => setFormData({ ...formData, contentType: val })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="PDF">PDF Document</SelectItem>
                                            <SelectItem value="TEXT">Plain Text / HTML</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {formData.contentType === 'PDF' ? (
                                <div className="space-y-4">
                                    <div className="flex flex-col gap-3">
                                        <Label>PDF Document</Label>
                                        <div className="flex items-center gap-4 p-4 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50/50">
                                            {formData.url ? (
                                                <div className="flex items-center gap-3 flex-1">
                                                    <div className="bg-emerald-100 p-2 rounded-lg">
                                                        <FileText className="h-6 w-6 text-emerald-600" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                            {formData.url.split('/').pop()}
                                                        </p>
                                                        <p className="text-xs text-emerald-600 flex items-center gap-1">
                                                            File ready to save
                                                        </p>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setFormData({ ...formData, url: '' })}
                                                        className="text-gray-400 hover:text-red-600"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-center justify-center w-full py-2">
                                                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                                                    <p className="text-sm text-gray-600 font-medium mb-1">
                                                        No file selected
                                                    </p>
                                                    <p className="text-xs text-gray-500 mb-3">
                                                        PDF files up to 50MB supported
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        <Label
                                            htmlFor="file-upload"
                                            className={`flex items-center justify-center px-4 py-3 border border-emerald-600 bg-emerald-50 rounded-md shadow-sm text-sm font-semibold text-emerald-700 hover:bg-emerald-100 cursor-pointer transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            {uploading ? (
                                                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                            ) : (
                                                <Upload className="h-5 w-5 mr-2" />
                                            )}
                                            {uploading ? 'Uploading...' : formData.url ? 'Replace PDF' : 'Upload PDF Document'}
                                        </Label>
                                        <input
                                            id="file-upload"
                                            type="file"
                                            className="hidden"
                                            accept=".pdf,application/pdf"
                                            onChange={handleFileUpload}
                                            disabled={uploading}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Label htmlFor="content">Minute Content (HTML supported)</Label>
                                    <Textarea
                                        id="content"
                                        value={formData.content}
                                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                        placeholder="Enter the meeting minutes here..."
                                        rows={10}
                                        required
                                    />
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={formLoading}>
                                {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {editingItem ? 'Update Minute' : 'Save Minute'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminMinutesPage;
